import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ScanFace, CheckCircle2, AlertTriangle, Loader2, RefreshCw, ArrowRight, Info } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Point, normalizeLandmarks } from './analyzeFace';
export type CaptureAngle = 'front' | 'angle45' | 'side';
export interface CapturedFrame {
  angle: CaptureAngle;
  imageSrc: string;
  landmarks: Point[];
}
interface CameraStepProps {
  currentAngle: CaptureAngle;
  captureIndex: number; // 1-based
  totalCaptures: number;
  previousCaptures: CapturedFrame[];
  onCapture: (frame: CapturedFrame) => void;
}
const FACE_API_SCRIPT = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.js';
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
let faceApiScriptPromise: Promise<any> | null = null;
function loadFaceApiScript(): Promise<any> {
  if ((window as any).faceapi) return Promise.resolve((window as any).faceapi);
  if (faceApiScriptPromise) return faceApiScriptPromise;
  faceApiScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = FACE_API_SCRIPT;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      const fa = (window as any).faceapi;
      if (fa) resolve(fa);else {
        faceApiScriptPromise = null;
        reject(new Error('face-api global not found'));
      }
    };
    script.onerror = () => {
      faceApiScriptPromise = null;
      reject(new Error('Failed to load face-api.js script'));
    };
    document.head.appendChild(script);
  });
  return faceApiScriptPromise;
}
// Simple SVG icon hint for which way to turn the head
const AngleIcon = ({
  angle,
  className = 'w-6 h-6'
}: {
  angle: CaptureAngle;
  className?: string;
}) => {
  if (angle === 'front') {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} data-id="element-216">
        <circle cx="12" cy="12" r="9" data-id="element-217" />
        <circle cx="9" cy="10" r="1" fill="currentColor" data-id="element-218" />
        <circle cx="15" cy="10" r="1" fill="currentColor" data-id="element-219" />
        <path d="M9 15c1 1 2 1.5 3 1.5s2-.5 3-1.5" strokeLinecap="round" data-id="element-220" />
      </svg>;
  }
  if (angle === 'angle45') {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} data-id="element-221">
        <path d="M14 3a9 9 0 0 1 0 18 7 7 0 0 1-3-1" strokeLinecap="round" data-id="element-222" />
        <circle cx="11" cy="10" r="1" fill="currentColor" data-id="element-223" />
        <path d="M11 15c.7.8 1.5 1.2 2.5 1.2" strokeLinecap="round" data-id="element-224" />
        <path d="M19 12h2M17 12l2 2M17 12l2-2" strokeLinecap="round" data-id="element-225" />
      </svg>;
  }
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} data-id="element-226">
      <path d="M15 3c4 2 5 6 5 9s-1 7-5 9c-3 1.5-7 0-8-3" strokeLinecap="round" data-id="element-227" />
      <circle cx="13" cy="10" r="1" fill="currentColor" data-id="element-228" />
      <path d="M13 15c.5.5 1 .7 1.5.7" strokeLinecap="round" data-id="element-229" />
    </svg>;
};
export function CameraStep({
  currentAngle,
  captureIndex,
  totalCaptures,
  previousCaptures,
  onCapture
}: CameraStepProps) {
  const {
    t
  } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detectionIntervalRef = useRef<number | null>(null);
  const faceapiRef = useRef<any>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [detectionStatus, setDetectionStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [faceDetected, setFaceDetected] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  // For front angle, require face detection. For 45/side, profile detection
  // is unreliable so we allow capture without strict detection.
  const requiresFaceDetection = currentAngle === 'front';
  const angleLabel = currentAngle === 'front' ? t('faceAnalysis.camera.angleFront') : currentAngle === 'angle45' ? t('faceAnalysis.camera.angle45') : t('faceAnalysis.camera.angleSide');
  const angleInstruction = currentAngle === 'front' ? t('faceAnalysis.camera.angleFrontInstruction') : currentAngle === 'angle45' ? t('faceAnalysis.camera.angle45Instruction') : t('faceAnalysis.camera.angleSideInstruction');
  const angleTip = currentAngle === 'front' ? t('faceAnalysis.camera.tipFront') : currentAngle === 'angle45' ? t('faceAnalysis.camera.tip45') : t('faceAnalysis.camera.tipSide');
  // Load face detection script + model
  useEffect(() => {
    let cancelled = false;
    setDetectionStatus('loading');
    const init = async () => {
      try {
        const faceapi = await loadFaceApiScript();
        if (cancelled) return;
        await Promise.all([faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL), faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)]);
        if (cancelled) return;
        faceapiRef.current = faceapi;
        setDetectionStatus('ready');
      } catch (err) {
        console.error('Face detection unavailable:', err);
        if (!cancelled) setDetectionStatus('error');
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [retryToken]);
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setHasPermission(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setHasPermission(false);
    }
  }, []);
  useEffect(() => {
    startCamera();
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
      if (detectionIntervalRef.current) {
        window.clearInterval(detectionIntervalRef.current);
      }
    };
  }, [startCamera]);
  useEffect(() => {
    if (detectionStatus !== 'ready' || !hasPermission || !faceapiRef.current) return;
    const faceapi = faceapiRef.current;
    const detect = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) return;
      try {
        const isMobile = window.innerWidth < 640;
        const inputSize = isMobile ? 224 : 320;
        const threshold = currentAngle === 'front' ? 0.5 : 0.3;
        const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({
          inputSize,
          scoreThreshold: threshold
        }));
        setFaceDetected(!!detection && detection.score >= threshold);
      } catch (err) {
        // ignore intermittent detection errors
      }
    };
    const interval = window.innerWidth < 640 ? 600 : 400;
    detectionIntervalRef.current = window.setInterval(detect, interval);
    return () => {
      if (detectionIntervalRef.current) {
        window.clearInterval(detectionIntervalRef.current);
      }
    };
  }, [detectionStatus, hasPermission, currentAngle]);
  const handleCaptureClick = () => {
    if (requiresFaceDetection && (detectionStatus !== 'ready' || !faceDetected)) {
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 2500);
      return;
    }
    if (detectionStatus !== 'ready') return;
    setCountdown(3);
    let count = 3;
    const timer = setInterval(() => {
      count -= 1;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(timer);
        setCountdown(null);
        executeCapture();
      }
    }, 1000);
  };
  const executeCapture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL('image/jpeg', 0.9);
    let landmarks: Point[] = [];
    const faceapi = faceapiRef.current;
    if (faceapi && currentAngle === 'front') {
      try {
        const isMobile = window.innerWidth < 640;
        const inputSize = isMobile ? 224 : 320;
        const result = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({
          inputSize,
          scoreThreshold: 0.4
        })).withFaceLandmarks();
        if (result && result.landmarks) {
          landmarks = normalizeLandmarks(result.landmarks.positions.map((p: any) => ({
            x: p.x,
            y: p.y
          })), video.videoWidth, video.videoHeight);
        }
      } catch (err) {
        // landmarks optional
      }
    }
    onCapture({
      angle: currentAngle,
      imageSrc,
      landmarks
    });
  };
  // Camera permission denied state
  if (hasPermission === false) {
    return <div className="bg-white p-8 rounded-2xl text-center max-w-md mx-auto shadow-xl" data-id="element-230">
        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-231">
          <Camera className="w-8 h-8" data-id="element-232" />
        </div>
        <h3 className="text-xl font-semibold mb-2" data-id="element-233">Camera Access Denied</h3>
        <p className="text-gray-600 mb-6" data-id="element-234">
          {t('faceAnalysis.error.cameraDenied')}
        </p>
        <button onClick={startCamera} className="bg-brand-copper text-white px-6 py-2 rounded-full font-medium hover:bg-brand-darkCopper transition-colors" data-id="element-235">
          {t('faceAnalysis.error.tryAgain')}
        </button>
      </div>;
  }
  if (detectionStatus === 'error') {
    return <div className="bg-white p-8 rounded-2xl text-center max-w-md mx-auto shadow-xl" data-id="element-236">
        <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-237">
          <AlertTriangle className="w-8 h-8" data-id="element-238" />
        </div>
        <h3 className="text-xl font-semibold mb-2" data-id="element-239">Detection Unavailable</h3>
        <p className="text-gray-600 mb-6" data-id="element-240">
          Could not load the face detection model. Please check your internet
          connection and try again.
        </p>
        <button onClick={() => setRetryToken(n => n + 1)} className="bg-brand-copper text-white px-6 py-2 rounded-full font-medium hover:bg-brand-darkCopper transition-colors inline-flex items-center" data-id="element-241">
          <RefreshCw className="w-4 h-4 mr-2" data-id="element-242" />
          {t('faceAnalysis.error.tryAgain')}
        </button>
      </div>;
  }
  // Status pill: only show face detection state for front; for profile show angle hint
  const getStatus = () => {
    if (detectionStatus === 'loading') {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" data-id="element-243" />,
        text: t('faceAnalysis.camera.loadingModel'),
        bg: 'bg-gray-700/80'
      };
    }
    if (!requiresFaceDetection) {
      return {
        icon: <AngleIcon angle={currentAngle} className="w-4 h-4" data-id="element-244" />,
        text: angleInstruction,
        bg: 'bg-brand-copper/90'
      };
    }
    if (faceDetected) {
      return {
        icon: <CheckCircle2 className="w-4 h-4" data-id="element-245" />,
        text: t('faceAnalysis.camera.faceDetected'),
        bg: 'bg-green-500/90'
      };
    }
    return {
      icon: <AlertTriangle className="w-4 h-4" data-id="element-246" />,
      text: t('faceAnalysis.camera.noFace'),
      bg: 'bg-amber-500/90'
    };
  };
  const status = getStatus();
  const captureEnabled = requiresFaceDetection ? detectionStatus === 'ready' && faceDetected : detectionStatus === 'ready';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="max-w-2xl mx-auto bg-gray-900 rounded-3xl overflow-hidden shadow-2xl relative" data-id="element-247">
      {/* Top Header — angle + progress */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 z-20 bg-gradient-to-b from-black/70 to-transparent" data-id="element-248">
        <div className="flex items-center justify-between text-white" data-id="element-249">
          <div className="flex items-center gap-2" data-id="element-250">
            <AngleIcon angle={currentAngle} className="w-5 h-5 text-brand-copper" data-id="element-251" />
            <div data-id="element-252">
              <div className="text-sm sm:text-base font-semibold" data-id="element-253">
                {angleLabel}
              </div>
              <div className="text-xs text-white/70 hidden sm:block" data-id="element-254">
                {angleInstruction}
              </div>
            </div>
          </div>
          <div className="text-xs sm:text-sm text-white/80 font-medium" data-id="element-255">
            {captureIndex} {t('faceAnalysis.camera.stepOf')} {totalCaptures}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5 mt-2.5" data-id="element-256">
          {Array.from({
          length: totalCaptures
        }).map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < captureIndex - 1 ? 'bg-green-400' : i === captureIndex - 1 ? 'bg-brand-copper' : 'bg-white/20'}`} data-id="element-257" />)}
        </div>
      </div>

      {/* Video Container */}
      <div className="relative h-[65vh] min-h-[420px] sm:h-auto sm:aspect-video bg-black" data-id="element-258">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{
        transform: 'scaleX(-1)'
      }} data-id="element-259" />
        <canvas ref={canvasRef} className="hidden" data-id="element-260" />

        {/* Animated Overlay - Scanning Frame */}
        <div className={`absolute inset-3 sm:inset-8 border-2 rounded-2xl z-10 pointer-events-none transition-colors duration-300 ${captureEnabled && requiresFaceDetection ? 'border-green-400/50' : 'border-brand-copper/30'}`} data-id="element-261">
          <div className={`absolute top-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-t-[3px] border-l-[3px] sm:border-t-4 sm:border-l-4 rounded-tl-xl transition-colors ${captureEnabled && requiresFaceDetection ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-262" />
          <div className={`absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-t-[3px] border-r-[3px] sm:border-t-4 sm:border-r-4 rounded-tr-xl transition-colors ${captureEnabled && requiresFaceDetection ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-263" />
          <div className={`absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-b-[3px] border-l-[3px] sm:border-b-4 sm:border-l-4 rounded-bl-xl transition-colors ${captureEnabled && requiresFaceDetection ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-264" />
          <div className={`absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-b-[3px] border-r-[3px] sm:border-b-4 sm:border-r-4 rounded-br-xl transition-colors ${captureEnabled && requiresFaceDetection ? 'border-green-400' : 'border-brand-copper'}`} data-id="element-265" />

          <motion.div animate={{
          top: ['0%', '100%', '0%']
        }} transition={{
          duration: 3,
          ease: 'linear',
          repeat: Infinity
        }} className="absolute left-0 right-0 h-0.5 bg-brand-copper/50 shadow-[0_0_15px_rgba(199,146,104,0.8)] z-10" data-id="element-266" />
        </div>

        {/* Status Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20" data-id="element-267">
          <motion.div key={status.text} initial={{
          y: 10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} className={`${status.bg} text-white backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium flex items-center shadow-lg max-w-[90vw]`} data-id="element-268">
            <span className="mr-2 flex-shrink-0" data-id="element-269">{status.icon}</span>
            <span className="truncate" data-id="element-270">{status.text}</span>
          </motion.div>
        </div>

        {/* Warning Toast */}
        <AnimatePresence data-id="element-271">
          {showWarning && <motion.div initial={{
          y: -20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -20,
          opacity: 0
        }} className="absolute top-24 left-1/2 -translate-x-1/2 z-30 bg-red-500 text-white px-5 py-3 rounded-xl shadow-xl flex items-center max-w-[90%]" data-id="element-272">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-273" />
              <span className="text-sm font-medium" data-id="element-274">
                {t('faceAnalysis.camera.notHuman')}
              </span>
            </motion.div>}
        </AnimatePresence>

        {/* Countdown */}
        {countdown !== null && <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/40 backdrop-blur-sm" data-id="element-275">
            <motion.div key={countdown} initial={{
          scale: 2,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.5,
          opacity: 0
        }} className="text-7xl font-bold text-white drop-shadow-lg" data-id="element-276">
              {countdown}
            </motion.div>
          </div>}
      </div>

      {/* Controls + Thumbnail Strip */}
      <div className="p-4 sm:p-6 bg-gray-900" data-id="element-277">
        {/* Scan guidance card — always visible description for current angle */}
        <motion.div key={currentAngle} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-4 bg-white/5 border border-white/10 rounded-2xl p-3.5 flex items-start gap-3" data-id="element-278">
          <div className="w-10 h-10 rounded-xl bg-brand-copper/20 flex items-center justify-center flex-shrink-0 text-brand-lightCopper" data-id="element-279">
            <AngleIcon angle={currentAngle} className="w-6 h-6" data-id="element-280" />
          </div>
          <div className="min-w-0" data-id="element-281">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-282">
              <span className="text-white font-semibold text-sm" data-id="element-283">
                {angleLabel}
              </span>
              <span className="text-[10px] font-medium text-brand-lightCopper bg-brand-copper/20 px-1.5 py-0.5 rounded-full" data-id="element-284">
                {captureIndex}/{totalCaptures}
              </span>
            </div>
            <p className="text-white/90 text-sm font-medium leading-snug" data-id="element-285">
              {angleInstruction}
            </p>
            <p className="text-white/50 text-xs mt-1 flex items-start gap-1 leading-snug" data-id="element-286">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" data-id="element-287" />
              <span data-id="element-288">{angleTip}</span>
            </p>
          </div>
        </motion.div>

        {/* Thumbnail strip of completed angles */}
        {previousCaptures.length > 0 && <div className="flex justify-center gap-2 mb-4" data-id="element-289">
            {previousCaptures.map(c => <div key={c.angle} className="relative w-12 h-16 rounded-lg overflow-hidden border-2 border-green-400" data-id="element-290">
                <img src={c.imageSrc} alt={c.angle} className="w-full h-full object-cover" style={{
            transform: 'scaleX(-1)'
          }} data-id="element-291" />
                <div className="absolute bottom-0 right-0 bg-green-400 text-white rounded-tl-md p-0.5" data-id="element-292">
                  <CheckCircle2 className="w-3 h-3" data-id="element-293" />
                </div>
              </div>)}
          </div>}

        <div className="flex justify-center items-center" data-id="element-294">
          <button onClick={handleCaptureClick} disabled={countdown !== null || detectionStatus !== 'ready'} className={`w-20 h-20 rounded-full border-4 flex items-center justify-center transition-all group ${captureEnabled ? 'border-green-400/50 hover:border-green-400' : 'border-white/20 cursor-not-allowed opacity-60'}`} data-id="element-295">
            <div className={`w-16 h-16 rounded-full transition-colors flex items-center justify-center ${captureEnabled ? 'bg-white group-hover:bg-green-400' : 'bg-gray-400'}`} data-id="element-296">
              <Camera className={`w-8 h-8 transition-colors ${captureEnabled ? 'text-gray-900 group-hover:text-white' : 'text-gray-600'}`} data-id="element-297" />
            </div>
          </button>
        </div>
      </div>
    </motion.div>;
}